import type { IconName } from "@/components/icons/Icon";
import {
  SideNavV2Item,
  SideNavV2Section,
  flattenSideNavItems,
  sideNavItemContainsId,
} from "@/components/interactive/SideNavV2";

// Keyed by `CATEGORY_NAV_SECTION_ORDER` ids, plus the admin section the side-nav model appends.
const PROJECT_NAV_CATEGORY_ICONS: Readonly<Record<string, IconName>> = {
  catalog: "libraryBig",
  data: "database",
  compute: "cpu",
  observability: "telescope",
  agents: "mousePointer2",
  more: "more",
  configuration: "cog",
  "admin-navigation": "lock",
};

export const getProjectNavCategoryIcon = (categoryId: string): IconName =>
  PROJECT_NAV_CATEGORY_ICONS[categoryId] ?? "more";

/** Whether a category lists the item anywhere, as a top-level row or as a child of one. */
export const projectNavCategoryContains = <T extends string>(
  category: SideNavV2Section<T>,
  itemId: T,
): boolean =>
  category.items.some((item) => item.id === itemId || sideNavItemContainsId(item, itemId));

export const findProjectNavCategory = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
  itemId: T | undefined,
): SideNavV2Section<T> | undefined => {
  if (itemId == null) return undefined;

  return categories.find((category) => projectNavCategoryContains(category, itemId));
};

/**
 * The categories a route's item id names a row in. Settings routes report their pane id, which can
 * repeat another category's id (Settings' Compute pane and Run are both "compute"), so theirs
 * names a row in Settings alone, unless another category lists that pane as its row (Data's
 * Vector store, More's Buckets). Returns `categories` itself when it does not narrow them.
 */
export const getProjectNavRouteCategories = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
  settingsRoute: boolean,
  routeItemId: T | undefined,
): readonly SideNavV2Section<T>[] => {
  if (!settingsRoute) return categories;
  const settings = categories.find((category) =>
    category.items.some((item) => item.id === "settings"),
  );
  if (settings == null) return categories;
  const listing =
    routeItemId == null ? undefined : findProjectNavCategoryListingRow(categories, routeItemId);
  return listing != null && listing !== settings ? categories : [settings];
};

/**
 * The category whose row leads to `path` or to the nearest page above it: the row with the longest
 * string href that is the path itself or one of its parents, ignoring queries and hashes.
 */
export const findProjectNavCategoryForPath = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
  path: string | undefined,
): SideNavV2Section<T> | undefined => {
  if (path == null) return undefined;

  const pathname = path.split(/[?#]/)[0] ?? path;
  let match: { category: SideNavV2Section<T>; hrefLength: number } | undefined;
  for (const category of categories) {
    for (const item of flattenSideNavItems(category.items)) {
      // Rows built from route helpers carry string hrefs; an object href names no path to compare.
      const href = typeof item.href === "string" ? item.href.split(/[?#]/)[0] : undefined;
      if (href == null || (pathname !== href && !pathname.startsWith(`${href}/`))) continue;
      if (match == null || href.length > match.hrefLength) {
        match = { category, hrefLength: href.length };
      }
    }
  }

  return match?.category;
};

const hasChildren = <T extends string>(item: SideNavV2Item<T>) => (item.children?.length ?? 0) > 0;

// Consecutive children that share a menu group label become one titled section; Settings panes
// carry their Settings group title there.
const groupByMenuGroupLabel = <T extends string>(
  sectionIdPrefix: string,
  items: readonly SideNavV2Item<T>[],
): SideNavV2Section<T>[] => {
  const sections: { id: string; label?: string; items: SideNavV2Item<T>[] }[] = [];

  for (const item of items) {
    const previous = sections.at(-1);
    if (previous != null && previous.label === item.menuGroupLabel) {
      previous.items.push(item);
      continue;
    }

    sections.push({
      id: `${sectionIdPrefix}:${item.menuGroupLabel ?? sections.length}`,
      label: item.menuGroupLabel,
      items: [item],
    });
  }

  return sections;
};

/**
 * Lays out one rail category as secondary-sidebar sections.
 *
 * A category that is a single parent (Run, Settings) lists that parent's children directly.
 * Otherwise leaf items lead, titled by their menu group labels when they carry one (Observe's
 * Monitoring), and every parent becomes a titled group of its children. Leaves go first because a
 * leaf placed after a group would read as that group's last child.
 */
export const getProjectNavCategorySidebarSections = <T extends string>(
  category: SideNavV2Section<T>,
): SideNavV2Section<T>[] => {
  const [onlyItem, ...otherItems] = category.items;
  if (onlyItem != null && otherItems.length === 0 && hasChildren(onlyItem)) {
    return groupByMenuGroupLabel(category.id, onlyItem.children ?? []);
  }

  const leaves = category.items.filter((item) => !hasChildren(item));
  const parents = category.items.filter(hasChildren);

  return [
    ...groupByMenuGroupLabel(`${category.id}:items`, leaves),
    ...parents.map((parent) => ({
      id: `${category.id}:${parent.id}`,
      label: parent.label,
      items: parent.children ?? [],
    })),
  ];
};

/**
 * The category whose sidebar lists `itemId` as a row, such as a pinned shortcut's. A parent its
 * sidebar shows only through its children does not count, so a row that shares an id with another
 * category's parent (Settings' Compute pane and Run are both "compute") finds its own.
 */
export const findProjectNavCategoryListingRow = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
  itemId: T,
): SideNavV2Section<T> | undefined =>
  categories.find((category) =>
    getProjectNavCategorySidebarSections(category).some((section) =>
      section.items.some((item) => item.id === itemId),
    ),
  );

const isNavigable = <T extends string>(item: SideNavV2Item<T>) =>
  item.href != null && !item.disabled;

/**
 * Where clicking a category's rail icon goes: the first page its sidebar lists. A parent lands on
 * its first visible child rather than its own destination, since flags can hide the child that
 * destination names.
 */
export const getProjectNavCategoryLandingItem = <T extends string>(
  category: SideNavV2Section<T>,
): SideNavV2Item<T> | undefined => {
  for (const item of category.items) {
    const child = item.children?.find(isNavigable);
    if (child != null) return child;
    if (isNavigable(item)) return item;
  }

  return undefined;
};

/**
 * The global sidebar state as the category nav reads it, with the reader's last show or hide
 * (`storedPinned`, kept across reloads) standing in for the open preference the page load reset.
 * The in-memory state starts open on every load and is aligned to the stored choice after the
 * first render, so until then this keeps the sidebar from opening for a frame and sliding shut.
 * Routes whose entry collapses the sidebar keep their own `sidebarOpen`.
 */
export const withStoredSidebarPreference = <
  S extends { sidebarOpen: boolean; sidebarOpenPreference?: boolean },
>(
  state: S,
  storedPinned: boolean | undefined,
  routeEntryCollapses: boolean,
): S =>
  storedPinned == null || state.sidebarOpenPreference === storedPinned
    ? state
    : {
        ...state,
        sidebarOpenPreference: storedPinned,
        sidebarOpen: routeEntryCollapses ? state.sidebarOpen : storedPinned,
      };

/**
 * Whether the category nav's secondary sidebar is pinned open beside the page.
 *
 * Ordinary routes share the global sidebar state. Routes that ask for a collapsed shell
 * (full-page views such as the SQL explorer) remember a pin only for that route, so opening it
 * there does not change the default everywhere else. Unpinned, those the route-entry collapse
 * (`GlobalAppStateListener`) covers still follow the global state, which it closes a frame after
 * they first paint: the sidebar animates shut from where the previous route left it, and reopens
 * on the way out when the listener restores the reader's preference. The rest start closed, since
 * nothing would close the global state there.
 *
 * Settings is the exception to the global route-entry collapse (`GlobalAppStateListener`), which
 * closes `sidebarOpen` there because the classic nav pairs Settings with its own secondary navbar.
 * Here the sidebar is the Settings navigation, so it follows the reader's preference, which that
 * collapse leaves alone.
 */
export const isProjectSecondarySidebarPinned = ({
  defaultCollapsed,
  routeEntryCollapses,
  settingsActive,
  routeKey,
  state,
}: {
  defaultCollapsed: boolean;
  /** Whether `GlobalAppStateListener` closes the global sidebar on entering this route. */
  routeEntryCollapses: boolean;
  settingsActive: boolean;
  routeKey: string;
  state: {
    sidebarOpen: boolean;
    sidebarOpenPreference?: boolean;
    sidebarClosedForSettings?: boolean;
    projectSecondarySidebarPinnedRouteKey?: unknown;
  };
}): boolean => {
  if (defaultCollapsed) {
    return (
      state.projectSecondarySidebarPinnedRouteKey === routeKey ||
      (routeEntryCollapses && state.sidebarOpen)
    );
  }
  if (settingsActive) return state.sidebarOpenPreference ?? state.sidebarOpen;
  // Just left Settings, where the sidebar showed: the route-entry collapse restores `sidebarOpen`
  // only after this page first renders, so the preference stands in, keeping the sidebar open
  // rather than closing it for a frame and sliding it back.
  if (state.sidebarClosedForSettings === true && !routeEntryCollapses) {
    return state.sidebarOpenPreference ?? state.sidebarOpen;
  }
  return state.sidebarOpen;
};
